import { useContext, useEffect, useMemo } from 'react';
import { IntlContext, type IntlShape } from 'react-intl';
import { defineMessages, type Message } from './define';
import { formatWith, type TFunction } from './runtime';
import { dateDay, dateLong, dateShort } from '../astra/lib/format';

/** Дата из каталога месяцев (`units.month.*`) — для языков перевода. */
function catalogDate(value: string | Date | null | undefined, style: DateStyle = 'long'): string {
  if (!value) return '—';
  const iso = value instanceof Date ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` : value;
  return style === 'day' ? dateDay(iso) : style === 'short' ? dateShort(iso) : dateLong(iso);
}
import { fallbackIntl, I18nContext } from './context';
import { formatDate, formatNumber, formatPercent, formatUnit, type DateStyle, type NumberOptions, type Unit } from './format';
import type { Locale } from './locales';

/** Подписи единиц — строки каталога: их переводят и вычитывают, как любой текст. */
export const UNIT_LABELS = defineMessages({
  ha: { id: 'units.ha', defaultMessage: 'га', description: 'Гектар, сокращение после числа: «12,5 га»' },
  t: { id: 'units.t', defaultMessage: 'т', description: 'Тонна, сокращение после числа: «3,4 т»' },
  m3: { id: 'units.m3', defaultMessage: 'м³', description: 'Кубометр воды, после числа: «1 200 м³»' },
});

/** Длинные формы с согласованием по числу (ICU plural): «21 гектар», «22 гектара», «25 гектаров». */
export const UNIT_LONG = defineMessages({
  ha: {
    id: 'units.ha.long',
    defaultMessage: '{count, plural, one {# гектар} few {# гектара} many {# гектаров} other {# гектара}}',
    description: 'Гектары словом в предложении; # — число по правилам языка',
  },
  contours: {
    id: 'units.contours',
    defaultMessage: '{count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}',
    description: 'Число контуров словом: «1 контур», «3 контура», «5 контуров»',
  },
});

/** `intl` текущего языка; без провайдера — язык-источник (не исключение). */
export function useIntlSafe(): IntlShape {
  return useContext(IntlContext) ?? fallbackIntl;
}

/** `formatMessage` текущего языка: `const t = useT(); t(messages.title)`. */
export function useT(): TFunction {
  const intl = useIntlSafe();
  return useMemo(() => ((message: Message, values?: object) => formatWith(intl, message, values)) as TFunction, [intl]);
}

/** Язык интерфейса и его переключение. */
export function useLocale(): { locale: Locale; locales: readonly Locale[]; setLocale: (locale: Locale) => void } {
  const { locale, locales, setLocale } = useContext(I18nContext);
  return { locale, locales, setLocale };
}

/**
 * Пространство имён экрана: просит провайдер загрузить его и говорит, готово
 * ли. Пока не готово, `formatMessage` отдаёт `defaultMessage` описания — текст
 * источника, а не id.
 */
export function useNamespace(namespace: string): boolean {
  const { namespaces, requestNamespace } = useContext(I18nContext);
  useEffect(() => requestNamespace(namespace), [namespace, requestNamespace]);
  return namespaces.has(namespace);
}

export interface Formatters {
  number: (value: number | null | undefined, options?: NumberOptions) => string;
  unit: (value: number | null | undefined, unit: Unit, options?: NumberOptions) => string;
  percent: (value: number | null | undefined, options?: NumberOptions) => string;
  date: (value: string | Date | null | undefined, style?: DateStyle) => string;
}

/** Форматтеры чисел, единиц и дат на языке интерфейса. */
export function useFormat(): Formatters {
  const intl = useIntlSafe();
  return useMemo(() => {
    const locale = intl.locale as Locale;
    return {
      number: (value, options) => formatNumber(locale, value, options),
      unit: (value, unit, options) => formatUnit(locale, value, unit, intl.formatMessage(UNIT_LABELS[unit]), options),
      percent: (value, options) => formatPercent(locale, value, options),
      // Узбекский — месяцы из каталога: CLDR `uz` есть не в каждом браузере
      // (урезанный ICU печатал бы английские месяцы), а в нём oʻ пишется через ‘.
      date: (value, style) => (locale === 'ru' ? formatDate(locale, value, style) : catalogDate(value, style)),
    };
  }, [intl]);
}
